/* eslint-disable react-hooks/exhaustive-deps */
import React, { cloneElement, useEffect, useRef } from 'react';
import useToggleClass from '../hooks/useToggleClass';
import Icons from './Icons';

interface IExpandableProps {
  children: React.ReactElement;
  title: React.ReactNode;
  closeOnContentElement: boolean;
  isInitiallyExpanded?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
  titleClassName?: string;
  contentClassName?: string;
  animation?: string;
  expandOn?: 'header' | 'arrow';
  arrow?: boolean;
  arrowType?:
    | 'down'
    | 'downMobile'
    | 'footer'
    | 'leftMobile'
    | 'right'
    | 'rightMobile'
    | 'up';
}

const ExpandablePanel = (props: IExpandableProps) => {
  const titleRef = useRef(null);
  const pannelRef = useRef(null);
  const arrowRef = useRef(null);
  const contentRef = useRef(null);

  const [internalIsExpanded, setInternalIsExpanded] = React.useState(
    !!props.isInitiallyExpanded,
  );
  const isExpanded = props.isExpanded ?? internalIsExpanded;
  const { addClass, removeClass } = useToggleClass();
  const {
    children,
    title,
    closeOnContentElement,
    titleClassName,
    contentClassName,
    animation,
    expandOn,
    arrow,
    arrowType,
  } = props;

  const toggleExpand = () => {
    if (props.isExpanded === undefined) {
      setInternalIsExpanded(prevValue => !prevValue);
    }

    props.onExpandedChange?.(!isExpanded);
  };

  const getArrowType = (type: string) => {
    const arrowTypes = new Map();
    arrowTypes.set('down', <Icons.ArrowDown />);
    arrowTypes.set('downMobile', <Icons.ArrowDownMobile />);
    arrowTypes.set('footer', <Icons.ArrowFooter />);
    arrowTypes.set('leftMobile', <Icons.ArrowLeftMobile />);
    arrowTypes.set('right', <Icons.ArrowRight />);
    arrowTypes.set('rightMobile', <Icons.ArrowRightMobile />);
    arrowTypes.set('up', <Icons.ArrowUp />);

    return arrowTypes.get(type);
  };

  const expandPanel = () => {
    removeClass('collapsed', pannelRef);
    removeClass('collapsed-content', contentRef);
    removeClass('collapsed', arrowRef);
    addClass('expanded-panel', pannelRef);
    addClass('expanded-content', contentRef);
    addClass('expanded', arrowRef);
    if (animation) addClass(animation, contentRef);
  };

  const collapsedPanel = () => {
    removeClass('expanded-panel', pannelRef);
    removeClass('expanded', arrowRef);
    addClass('collapsed', pannelRef);
    addClass('collapsed', arrowRef);
    if (animation) {
      removeClass(animation, contentRef);
      setTimeout(() => {
        removeClass('expanded-content', contentRef);
        addClass('collapsed-content', contentRef);
      }, 300);
    } else {
      removeClass('expanded-content', contentRef);
      addClass('collapsed-content', contentRef);
    }
  };

  const childrenContent = closeOnContentElement
    ? cloneElement(children, { toggleExpand })
    : children;

  useEffect(() => {
    if (contentClassName) addClass(contentClassName, contentRef);
  }, []);

  useEffect(() => {
    if (isExpanded) {
      expandPanel();
    } else {
      collapsedPanel();
    }
  }, [isExpanded]);

  return (
    <div className={'expand-panel'} ref={pannelRef}>
      <div
        className={`expand-panel-title ${titleClassName}`.trim()}
        onClick={expandOn === 'header' ? () => toggleExpand() : undefined}
        ref={titleRef}
      >
        <div className="full-width">{title}</div>
        {arrow && arrowType && (
          <div
            className={'expand-panel-arrow'}
            onClick={expandOn === 'arrow' ? () => toggleExpand() : undefined}
            ref={arrowRef}
          >
            {getArrowType(arrowType)}
          </div>
        )}
      </div>
      <div className={'expand-panel-content'} ref={contentRef}>
        <div>{childrenContent}</div>
      </div>
    </div>
  );
};

ExpandablePanel.defaultProps = {
  expandOn: 'arrow',
  arrow: true,
  arrowType: 'down',
  closeOnContentElement: false,
};

export default ExpandablePanel;
